import equal from 'fast-deep-equal';
import moment from 'moment';
import { parse as parseQueryStringParams, stringify as toQueryString } from '@AppModels/QueryStringUtils';
import * as React from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { PersistentStorageManager } from '../../../models/Storage';

const getParamsFromQueryString = (search?: string) =>
	parseQueryStringParams(search || '') as {
		automationId?: string;
		userId?: string;
		date?: string;
		policies?: string;
	};
const getDateRangeFromParams = (params: { userId?: string; date?: string; policies?: string }) => {
	const { date } = params;
	if (date) {
		const range: { startDate?: Date; endDate?: Date } = {};
		const dateAsMoment = moment(date);
		range.endDate = dateAsMoment.endOf('month').toDate();
		range.startDate = dateAsMoment.startOf('month').toDate();
		return range;
	}
	return {
		endDate: moment().endOf('month').toDate(),
		startDate: moment().startOf('month').toDate(),
	};
};

const BirthdayReportPersistentStoreKey = 'BirthdayReportPageState';

export const useReportingUrlSearchParams = () => {
	const history = useHistory();
	const [params, setParams] = React.useState(getParamsFromQueryString(history.location.search));
	const match = useRouteMatch<{ automationId?: string; userId?: string }>();
	const automationId = match?.params?.automationId;

	React.useEffect(() => {
		const nextParams = getParamsFromQueryString(history.location.search);
		if (nextParams?.date !== params?.date) {
			setParams(nextParams);
		}
	}, [history.location.search, params?.date]);

	const searchParamsDateRange = React.useMemo(() => {
		return getDateRangeFromParams(params);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [params?.date]);

	const setUrlDate = React.useCallback(
		(dateRange?: { startDate?: Date; endDate?: Date }) => {
			const { date, ...restParams } = getParamsFromQueryString(history.location.search);
			const nextParams = {
				...restParams,

				date: moment(dateRange.startDate).toISOString(),
			};
			setParams(nextParams);
			history.push({
				...history.location,
				search: toQueryString({
					...restParams,

					date: moment(dateRange.startDate).toISOString(),
				}),
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[history.location, history.location.search]
	);

	const setUrlUserId = React.useCallback(
		(changedUserId?: string) => {
			const { userId, ...restParams } = getParamsFromQueryString(history.location.search || '');
			if (!equal(userId, changedUserId)) {
				const nextParams = { ...restParams, userId: changedUserId };
				setParams(nextParams);
				history.push({
					...history.location,
					search: toQueryString(nextParams),
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[history.location, history.location.search]
	);

	return {
		automationId,
		history,
		params,
		searchParamsDateRange,
		setUrlDate,
		setUrlUserId,
	} as const;
};

export const useAutomationReportUrlSearchParams = () => {
	const { automationId, history, params, searchParamsDateRange, setUrlDate, setUrlUserId } =
		useReportingUrlSearchParams();
	const match = useRouteMatch();
	const baseUrl = match.path.split('/:')[0];
	// const params = useParams<{ userId?: string }>();

	return {
		automationId,
		baseUrl,
		history,
		match,
		params,
		searchParamsDateRange,
		setUrlDate,
		setUrlUserId,
	} as const;
};

export const useBirthdayReportUrlSearchParams = () => {
	const locationParams = useParams<{ tab?: string }>();
	const tabName = (locationParams.tab || '').toLocaleLowerCase();
	const { history, params, searchParamsDateRange, setUrlDate, setUrlUserId } = useReportingUrlSearchParams();
	const match = useRouteMatch();
	const baseUrl = match.path.split('/:')[0];

	const [persistentPageState, setPageState] = React.useState<{
		tabName?: string;
	}>(JSON.parse(PersistentStorageManager.local.get(BirthdayReportPersistentStoreKey) || '{}'));
	const setPersistentPageState = React.useCallback((pageState?: { tabName?: string }) => {
		PersistentStorageManager.local.set(BirthdayReportPersistentStoreKey, JSON.stringify(pageState || '{}'));

		setPageState(pageState);
	}, []);
	return {
		baseUrl,
		history,
		match,
		params,
		persistentPageState,
		searchParamsDateRange,
		setPersistentPageState,
		setUrlDate,
		setUrlUserId,
		tabName: tabName || persistentPageState?.tabName,
	} as const;
};

export const useResourceSelectorReportUrlSearchParams = () => {
	const baseParams = useReportingUrlSearchParams();
	const { history, searchParamsDateRange, params } = baseParams;

	const urlPolicies = params?.policies?.split(',');
	const setUrlPolicies = React.useCallback(
		(policies?: string[]) => {
			const urlUserId = getParamsFromQueryString(history.location.search)?.userId;
			history.push({
				...history.location,
				search: toQueryString({
					date: moment(searchParamsDateRange.startDate).toISOString(),
					policies: policies?.reduce((res, x) => `${res}${res ? ',' : ''}${x}`, ''),
					userId: urlUserId,
				}),
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[history.location, history.location.search, searchParamsDateRange]
	);

	return {
		...baseParams,
		setUrlPolicies,
		urlPolicies,
	} as const;
};

export const useContactDrawerState = () => {
	const [activeDrawer, setActiveDrawer] = React.useState<string>('');
	const [drawerContactId, setDrawerContactId] = React.useState<string | null>(null);

	const setDrawerToContactInfo = React.useCallback((contactId: string) => {
		setDrawerContactId(contactId);
		setActiveDrawer('contact-info');
	}, []);

	const handleDrawerValueChange = React.useCallback((value: string) => {
		if (!value) {
			setActiveDrawer('');
			setDrawerContactId(null);
			return;
		}
		setActiveDrawer(value);
	}, []);

	return { activeDrawer, drawerContactId, setDrawerToContactInfo, handleDrawerValueChange };
};
